import { type FC, useEffect } from "react";

import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { useOptimizedFullSleep } from "@/domains/health/oura/hooks/useOptimizedFullSleep";
import { useOptimizedSleep } from "@/domains/health/oura/hooks/useOptimizedSleep";
import { debugHealth } from "@/domains/logging/utils/debugLog";

// WHAT: Detailseite für Oura-Schlafdaten mit Score, Phasen, Effizienz, Latency und Herzfrequenz-/HRV-Analyse
// WHY:  Liefert eine tiefgehende Sicht auf Schlafqualität und -architektur zur Optimierung von Erholung und Gesundheit
// DOMAIN: health → oura → pages
// DEPENDENCIES: `useOptimizedSleep`, `useOptimizedFullSleep`, TracQ Card/Badge/Progress-Bar, `debugHealth`
// RISKS:
//   - NOTE: Bewertungslogik (z.B. Thresholds für Effizienz, Latency, Phasenanteile) ist heuristisch und sollte medizinisch validiert werden

interface SleepContributorsScore {
  restfulness?: number;
  timing?: number;
}

const isSleepContributorsScore = (value: unknown): value is SleepContributorsScore =>
  typeof value === "object" && value !== null;

/**
 * WHAT:
 *   Generic helper function for percentage-based ratings (Timing, Restfulness).
 *
 * WHY:
 *   Centralizes mapping von Prozentwerten (0–100) auf Badge-Varianten
 *   und Labels, sodass die Logik konsistent in der gesamten Komponente
 *   wiederverwendet werden kann.
 *
 * INPUTS:
 *   - percentage: number — Wert zwischen 0 und 100
 *
 * OUTPUT:
 *   - { variant, label } — Variant-Key für Badge-Komponenten und Textlabel.
 *
 * @param {number} percentage - Percentage value (0-100)
 *
 * @returns {{label: string, variant: string}} Object with variant and label for badge display
 *
 */
const getPercentageRating = (percentage: number): { label: string; variant: BadgeVariant } => {
  if (percentage >= 85) {
    return { label: "Optimal", variant: "success" };
  } // 85-100% - Optimal (Grün)
  if (percentage >= 70) {
    return { label: "Good", variant: "info" };
  } // 70-84% - Good (Blau)
  if (percentage >= 60) {
    return { label: "Fair", variant: "warning" };
  } // 60-69% - Fair (Gelb)
  return { label: "Poor", variant: "destructive" }; // 0-59% - Poor (Rot)
};

/**
 * WHAT:
 *   Formats duration in seconds to human-readable hours and minutes format.
 *
 * WHY:
 *   Provides consistent duration display across the component.
 *
 * @param {number} [seconds] - Duration in seconds (optional)
 *
 * @returns {string} Formatted duration (e.g., "7h 30m") or "N/A" if invalid
 *
 */
const formatDuration = (seconds?: number): string => {
  if (!seconds) {
    return "N/A";
  }
  // NOTE: Calculate hours and minutes from total seconds
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${String(hours)}h ${String(minutes)}m`;
};

/**
 * WHAT:
 *   Calculates percentage of sleep phase relative to total sleep duration.
 *
 * WHY:
 *   Provides percentage metrics for sleep phase visualization and assessment.
 *
 * @param {number} [phaseSeconds] - Sleep phase duration in seconds
 *
 * @param {number} [totalSeconds] - Total sleep duration in seconds
 *
 * @returns {number} Percentage (0-100) rounded to nearest integer
 *
 */
const calculateSleepPercentage = (phaseSeconds?: number, totalSeconds?: number) => {
  // NOTE: Validate inputs - prevent division by zero
  if (!phaseSeconds || !totalSeconds || totalSeconds === 0) {
    return 0;
  }
  // NOTE: Round to nearest integer for display
  return Math.round((phaseSeconds / totalSeconds) * 100);
};

/**
 * WHAT:
 *   Determines badge variant based on sleep duration in hours.
 *
 * WHY:
 *   Provides visual feedback on sleep duration quality.
 *
 * @param {number} [seconds] - Sleep duration in seconds
 *
 * @returns {string} Badge variant ("success", "warning", or "destructive")
 *
 */
type BadgeVariant =
  | "accent"
  | "default"
  | "destructive"
  | "info"
  | "outline"
  | "primary"
  | "success"
  | "warning";

const getSleepDurationBadgeVariant = (seconds?: number): BadgeVariant => {
  if (!seconds) {
    return "destructive";
  }
  const hours = seconds / 3600;
  if (hours >= 7.5) {
    return "success";
  }
  if (hours >= 6.5) {
    return "warning";
  }
  return "destructive";
};

/**
 * WHAT:
 *   Determines badge variant for sleep phases (REM, Deep) based on percentage.
 *
 * WHY:
 *   Provides visual feedback on sleep phase distribution quality.
 *
 * @param {number} percentage - Sleep phase percentage (0-100)
 *
 * @returns {string} Badge variant ("success", "info", "warning", or "destructive")
 *
 */
const getSleepPhaseBadgeVariant = (percentage: number): BadgeVariant => {
  if (percentage >= 25) {
    return "success";
  }
  if (percentage >= 20) {
    return "info";
  }
  if (percentage >= 17) {
    return "warning";
  }
  return "destructive";
};

/**
 * WHAT:
 *   Determines badge variant for light sleep based on percentage.
 *
 * WHY:
 *   Provides visual feedback on light sleep distribution quality.
 *
 * @param {number} percentage - Light sleep percentage (0-100)
 *
 * @returns {string} Badge variant ("success", "info", "warning", or "destructive")
 *
 */
const getLightSleepBadgeVariant = (percentage: number): BadgeVariant => {
  if (percentage >= 45 && percentage <= 55) {
    return "success";
  } // ✅ Optimal: 45-55%
  if ((percentage >= 40 && percentage < 45) || (percentage > 55 && percentage <= 60)) {
    return "info";
  } // 🔵 Good: 40-45% oder 55-60%
  if ((percentage >= 35 && percentage < 40) || (percentage > 60 && percentage <= 65)) {
    return "warning";
  } // 🟡 Acceptable: 35-40% oder 60-65%
  return "destructive"; // 🔴 Poor: <35% oder >65%
};

/**
 * WHAT:
 *   Determines rating for minimum heart rate during sleep.
 *
 * WHY:
 *   Provides visual feedback on heart rate recovery quality.
 *
 * @param {number} bpm - Minimum heart rate in beats per minute
 *
 * @returns {{label: string, variant: string}} Object with variant and label for badge display
 *
 */
const getMinHeartRateRating = (bpm: number): { label: string; variant: BadgeVariant } => {
  if (bpm <= 44) {
    return { label: "Excellent", variant: "success" };
  } // <= 44 - Grün (Excellent)
  if (bpm <= 50) {
    return { label: "Good", variant: "info" };
  } // 45-50 - Blau (Good)
  if (bpm <= 55) {
    return { label: "Fair", variant: "warning" };
  } // 51-55 - Gelb (Fair)
  return { label: "Poor", variant: "destructive" }; // > 55 - Rot (Poor)
};

/**
 * WHAT:
 *   Determines rating for breathing rate during sleep.
 *
 * WHY:
 *   Provides visual feedback on breathing rate quality.
 *
 * @param {number} breathsPerMin - Breathing rate in breaths per minute
 *
 * @returns {{label: string, variant: string}} Object with variant and label for badge display
 *
 */
const getBreathingRateRating = (
  breathsPerMin: number,
): { label: string; variant: BadgeVariant } => {
  if (breathsPerMin < 16) {
    return { label: "Excellent", variant: "success" };
  } // < 16 - Grün (Excellent)
  if (breathsPerMin <= 16.5) {
    return { label: "Good", variant: "info" };
  } // 16-16.5 - Blau (Good)
  if (breathsPerMin < 17.3) {
    return { label: "Fair", variant: "warning" };
  } // 16.5-17.3 - Gelb (Fair)
  return { label: "Poor", variant: "destructive" }; // >= 17.3 - Rot (Poor)
};

/**
 * WHAT:
 *   Renders sleep score badge based on score value.
 *
 * WHY:
 *   Reduces cognitive complexity in main component by extracting conditional rendering logic.
 *
 * @param {number} sleepScore - Sleep score value (0-100)
 *
 * @returns {null | React.ReactElement} Badge component or null
 *
 */
const renderSleepScoreBadge = (sleepScore: number): null | React.ReactElement => {
  if (sleepScore >= 80) {
    return (
      <Badge className="text-sm" variant="success">
        🟢 Ausgezeichnet
      </Badge>
    );
  }
  if (sleepScore >= 60) {
    return (
      <Badge className="text-sm" variant="info">
        🔵 Gut
      </Badge>
    );
  }
  if (sleepScore >= 40) {
    return (
      <Badge className="text-sm" variant="warning">
        🟡 Fair
      </Badge>
    );
  }
  return (
    <Badge className="text-sm" variant="destructive">
      🔴 Schlecht
    </Badge>
  );
};

/**
 * WHAT:
 *   Renders sleep insights based on sleep score and data.
 *
 * WHY:
 *   Reduces cognitive complexity in main component by extracting conditional rendering logic.
 *
 * @param {number} sleepScore - Sleep score value (0-100)
 *
 * @param {unknown} combinedSleepData - Combined sleep data object
 *
 * @param {unknown} timingScore - Timing score value
 *
 * @returns {React.ReactElement[]} Array of insight paragraph elements
 *
 */
const renderSleepInsights = (
  sleepScore: number,
  combinedSleepData: unknown,
  timingScore: unknown,
): React.ReactElement[] => {
  const insights: React.ReactElement[] = [];

  if (sleepScore >= 80) {
    insights.push(<p key="excellent">🌙 Ausgezeichneter Schlaf! Du bist optimal erholt.</p>);
  } else if (sleepScore >= 60) {
    insights.push(<p key="good">😴 Guter Schlaf. Achte auf konsistente Schlafzeiten.</p>);
  } else if (sleepScore >= 40) {
    insights.push(
      <p key="moderate">😪 Moderate Schlafqualität. Verbessere deine Schlafhygiene.</p>,
    );
  } else {
    insights.push(<p key="poor">😴 Schlechter Schlaf. Fokus auf Schlafoptimierung nötig.</p>);
  }

  const typedCombined = combinedSleepData as null | {
    efficiency?: number;
    latency?: number;
  };

  if (typedCombined?.efficiency && typedCombined.efficiency < 85) {
    insights.push(<p key="efficiency">⚠️ Deine Schlafeffizienz könnte verbessert werden.</p>);
  }

  if (typedCombined?.latency) {
    if (typedCombined.latency < 300) {
      insights.push(
        <p key="latency-fast">
          ⚡ Sehr schnelles Einschlafen (unter 5 min). Möglicherweise zu hoher Schlafdruck.
        </p>,
      );
    } else if (typedCombined.latency > 1200) {
      insights.push(
        <p key="latency-slow">
          ⏰ Lange Einschlafzeit (über 20 min). Entspannungstechniken könnten helfen.
        </p>,
      );
    }
  }

  if (typeof timingScore === "number" && timingScore < 60) {
    insights.push(
      <p key="timing">📅 Schlechtes Schlaf-Timing. Versuche regelmäßigere Schlafzeiten.</p>,
    );
  }

  return insights;
};

/**
 * WHAT:
 *   Builds debug data object for fullSleep logging.
 *
 * WHY:
 *   Reduces cognitive complexity in useEffect by extracting complex object construction.
 *
 * @param {unknown} fullSleep - Full sleep data object
 *
 * @param {unknown} combinedSleepData - Combined sleep data object
 *
 * @param {null | number} minHeartRate - Minimum heart rate value
 *
 * @returns {null | Record<string, unknown>} Debug data object or null
 *
 */
const buildFullSleepDebugData = (
  fullSleep: unknown,
  combinedSleepData: unknown,
  minHeartRate: null | number,
): null | Record<string, unknown> => {
  if (typeof fullSleep !== "object" || fullSleep === null) {
    return null;
  }
  const typedFullSleep = fullSleep as {
    average_breath?: number;
    breath?: unknown;
    heart_rate?: unknown;
    hrv?: unknown;
  };
  const typedCombined = combinedSleepData as null | {
    deep_sleep_duration?: number;
    efficiency?: number;
    latency?: number;
    light_sleep_duration?: number;
    rem_sleep_duration?: number;
    total_sleep_duration?: number;
  };

  const totalDuration = typedCombined?.total_sleep_duration;
  const hasTotalDuration = typeof totalDuration === "number";

  return {
    averageBreath: typedFullSleep.average_breath,
    breathingRateRating: typedFullSleep.average_breath
      ? getBreathingRateRating(typedFullSleep.average_breath)
      : null,
    deepBadgeVariant: hasTotalDuration
      ? getSleepPhaseBadgeVariant(
          calculateSleepPercentage(typedCombined?.deep_sleep_duration, totalDuration),
        )
      : "destructive",
    deepPercentage: hasTotalDuration
      ? calculateSleepPercentage(typedCombined?.deep_sleep_duration, totalDuration)
      : 0,
    deepSleepDuration: typedCombined?.deep_sleep_duration,
    efficiency: typedCombined?.efficiency,
    hasBreath: !!typedFullSleep.breath,
    hasData: true,
    hasHeartRate: !!typedFullSleep.heart_rate,
    hasHrv: !!typedFullSleep.hrv,
    latency: typedCombined?.latency,
    latencyMinutes: typedCombined?.latency ? Math.round(typedCombined.latency / 60) : 0,
    lightBadgeVariant: hasTotalDuration
      ? getLightSleepBadgeVariant(
          calculateSleepPercentage(typedCombined?.light_sleep_duration, totalDuration),
        )
      : "destructive",
    lightPercentage: hasTotalDuration
      ? calculateSleepPercentage(typedCombined?.light_sleep_duration, totalDuration)
      : 0,
    lightSleepDuration: typedCombined?.light_sleep_duration,
    minHeartRate,
    minHeartRateRating: minHeartRate ? getMinHeartRateRating(minHeartRate) : null,
    remBadgeVariant: hasTotalDuration
      ? getSleepPhaseBadgeVariant(
          calculateSleepPercentage(typedCombined?.rem_sleep_duration, totalDuration),
        )
      : "destructive",
    remPercentage: hasTotalDuration
      ? calculateSleepPercentage(typedCombined?.rem_sleep_duration, totalDuration)
      : 0,
    remSleepDuration: typedCombined?.rem_sleep_duration,
    totalSleepDuration: totalDuration,
  };
};

/**
 * WHAT:
 *   Builds debug data object for sleep logging.
 *
 * WHY:
 *   Reduces cognitive complexity in useEffect by extracting complex object construction.
 *
 * @param {unknown} sleep - Sleep data object
 *
 * @param {unknown} restfulnessScore - Restfulness score value
 *
 * @param {unknown} restfulnessRating - Restfulness rating object
 *
 * @param {unknown} timingScore - Timing score value
 *
 * @param {unknown} timingRating - Timing rating object
 *
 * @returns {null | Record<string, unknown>} Debug data object or null
 *
 */
const buildSleepDebugData = (
  sleep: unknown,
  restfulnessScore: unknown,
  restfulnessRating: unknown,
  timingScore: unknown,
  timingRating: unknown,
): null | Record<string, unknown> => {
  if (typeof sleep !== "object" || sleep === null) {
    return null;
  }
  const typedSleep = sleep as { contributors?: unknown; score?: unknown };
  return {
    contributors:
      typeof typedSleep.contributors === "object" && typedSleep.contributors !== null
        ? (typedSleep.contributors as SleepContributorsScore)
        : undefined,
    hasData: true,
    restfulness: restfulnessScore,
    restfulnessRating,
    score: typedSleep.score,
    timing: timingScore,
    timingRating,
  };
};

/**
 * WHAT:
 *   Stellt den Sleep Score samt Phasenverteilung, Effizienz, Einschlaflatenz sowie HRV- und Herzfrequenzmetrik
 *   in einer ausführlichen Detailansicht dar.
 *
 * WHY:
 *   Unterstützt eine datengetriebene Analyse der Schlafqualität über die einfache Score-Anzeige hinaus.
 *
 * @returns {JSX.Element | null} Sleep detail component with analysis and metrics
 *
 */
export const SleepDetail: FC = () => {
  const {
    error: sleepError,
    loading: sleepLoading,
    score: sleepScore,
    sleep,
  } = useOptimizedSleep();

  const {
    averageHeartRate,
    averageHRV,
    error: fullSleepError,
    fullSleep,
    loading: fullSleepLoading,
    maxHRV,
  } = useOptimizedFullSleep();

  const rawContributors: unknown = sleep
    ? (sleep as { contributors?: unknown }).contributors
    : undefined;
  const timingScore =
    isSleepContributorsScore(rawContributors) && typeof rawContributors.timing === "number"
      ? rawContributors.timing
      : undefined;
  const timingRating = timingScore === undefined ? null : getPercentageRating(timingScore);

  const restfulnessScore =
    isSleepContributorsScore(rawContributors) && typeof rawContributors.restfulness === "number"
      ? rawContributors.restfulness
      : undefined;
  const restfulnessRating =
    restfulnessScore === undefined ? null : getPercentageRating(restfulnessScore);

  // Combine sleep and fullSleep data for fields that exist in both
  // NOTE: Some fields like total_sleep_duration are in OuraSleep, not OuraFullSleep
  const combinedSleepData = sleep ?? null;

  /**
   * WHAT:
   *   Calculates minimum heart rate from heart rate data array.
   *
   * WHY:
   *   Provides minimum heart rate metric for sleep quality assessment.
   *
   * BEHAVIOR:
   *   - Filters out null/undefined values
   *   - Finds minimum value using Math.min
   *
   * RISKS:
   *   - NOTE: May return undefined if no valid heart rate data available
   *   - NOTE: Math.min with empty array returns Infinity - filtered array prevents this
   */
  const minHeartRate = (() => {
    const heartRateItems = fullSleep?.heart_rate?.items;
    if (!heartRateItems) {
      return null;
    }
    const validHeartRates = heartRateItems.filter((hr): hr is number => typeof hr === "number");
    if (validHeartRates.length === 0) {
      return null;
    }
    return Math.min(...validHeartRates);
  })();

  const loading = sleepLoading || fullSleepLoading;
  const errorMessage: null | string = (() => {
    if (sleepError && typeof sleepError === "string") {
      return sleepError;
    }
    if (fullSleepError && typeof fullSleepError === "string") {
      return fullSleepError;
    }
    return null;
  })();

  // Debug: Log available data in useEffect to avoid setState during render
  useEffect(() => {
    if (!sleep && !fullSleep) {
      return;
    }

    const fullSleepDebug = buildFullSleepDebugData(fullSleep, combinedSleepData, minHeartRate);
    const sleepDebug = buildSleepDebugData(
      sleep,
      restfulnessScore,
      restfulnessRating,
      timingScore,
      timingRating,
    );

    debugHealth("info", "SleepDetail", "Sleep data loaded", {
      fullSleep: fullSleepDebug,
      sleep: sleepDebug,
    });
  }, [
    sleep,
    fullSleep,
    minHeartRate,
    combinedSleepData,
    timingScore,
    timingRating,
    restfulnessScore,
    restfulnessRating,
  ]);

  if (loading || errorMessage || (!sleep && !fullSleep)) {
    return null; // OuraApp handles loading/error states globally
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-neutral-100 mb-2">Sleep Score</h2>
        <p className="text-neutral-400">Wie gut hast du geschlafen?</p>
      </div>

      {/* Main Score */}
      {sleep ? (
        <Card className="text-center" padding="lg" variant="default">
          <div className="text-6xl font-bold text-neutral-100 mb-2">{sleepScore}</div>
          <div className="text-neutral-400 mb-4">von 100</div>

          {/* Score Status */}
          <div className="mt-4">{renderSleepScoreBadge(sleepScore)}</div>
        </Card>
      ) : null}

      {/* Sleep Phases */}
      {combinedSleepData ? (
        <Card padding="lg" variant="default">
          <h3 className="text-lg font-semibold text-neutral-200 mb-4">Schlafphasen</h3>
          <div className="grid gap-4">
            <div className="flex items-center justify-between">
              <span className="text-neutral-300">Gesamtschlaf</span>
              <Badge
                className="font-medium"
                variant={getSleepDurationBadgeVariant(combinedSleepData.total_sleep_duration)}
              >
                {formatDuration(combinedSleepData.total_sleep_duration)}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-neutral-300">REM-Schlaf</span>
              <Badge
                className="font-medium"
                variant={getSleepPhaseBadgeVariant(
                  calculateSleepPercentage(
                    combinedSleepData.rem_sleep_duration,
                    combinedSleepData.total_sleep_duration,
                  ),
                )}
              >
                {formatDuration(combinedSleepData.rem_sleep_duration)} |{" "}
                {String(
                  calculateSleepPercentage(
                    combinedSleepData.rem_sleep_duration,
                    combinedSleepData.total_sleep_duration,
                  ),
                )}
                %
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-neutral-300">Tiefschlaf</span>
              <Badge
                className="font-medium"
                variant={getSleepPhaseBadgeVariant(
                  calculateSleepPercentage(
                    combinedSleepData.deep_sleep_duration,
                    combinedSleepData.total_sleep_duration,
                  ),
                )}
              >
                {formatDuration(combinedSleepData.deep_sleep_duration)} |{" "}
                {calculateSleepPercentage(
                  combinedSleepData.deep_sleep_duration,
                  combinedSleepData.total_sleep_duration,
                )}
                %
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-neutral-300">Leichtschlaf</span>
              <Badge
                className="font-medium"
                variant={getLightSleepBadgeVariant(
                  calculateSleepPercentage(
                    combinedSleepData.light_sleep_duration,
                    combinedSleepData.total_sleep_duration,
                  ),
                )}
              >
                {formatDuration(combinedSleepData.light_sleep_duration)} |{" "}
                {calculateSleepPercentage(
                  combinedSleepData.light_sleep_duration,
                  combinedSleepData.total_sleep_duration,
                )}
                %
              </Badge>
            </div>
          </div>
        </Card>
      ) : null}

      {/* Sleep Quality */}
      {sleep ? (
        <Card padding="lg" variant="default">
          <h3 className="text-lg font-semibold text-neutral-200 mb-4">Schlafqualität</h3>
          <div className="grid gap-4">
            <div className="flex items-center justify-between">
              <span className="text-neutral-300">Schlafeffizienz</span>
              <Badge
                className="font-medium"
                variant={(() => {
                  const efficiency = combinedSleepData?.efficiency ?? 0;
                  if (efficiency >= 85) {
                    return "success";
                  }
                  if (efficiency >= 70) {
                    return "warning";
                  }
                  return "destructive";
                })()}
              >
                {combinedSleepData?.efficiency ?? 0}%
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-neutral-300">Einschlafzeit</span>
              <Badge
                className="font-medium"
                variant={(() => {
                  const latency = combinedSleepData?.latency;
                  if (!latency) {
                    return "default";
                  }
                  if (latency < 300) {
                    return "destructive"; // < 5 min - Rot (zu hoher Schlafdruck)
                  }
                  if (latency <= 480) {
                    return "warning"; // 5-8 min - Gelb
                  }
                  if (latency <= 900) {
                    return "success"; // 9-15 min - Grün (optimal)
                  }
                  if (latency <= 1200) {
                    return "info"; // 16-20 min - Blau
                  }
                  return "destructive"; // >20 min - Rot
                })()}
              >
                {combinedSleepData?.latency
                  ? `${String(Math.round(combinedSleepData.latency / 60))} min`
                  : "N/A"}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-neutral-300">Timing</span>
              <Badge
                className="font-medium"
                variant={(timingRating as null | { variant?: BadgeVariant })?.variant ?? "default"}
              >
                {timingRating?.label ?? "N/A"}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-neutral-300">Restfulness</span>
              <Badge
                className="font-medium"
                variant={
                  (restfulnessRating as null | { variant?: BadgeVariant })?.variant ?? "default"
                }
              >
                {restfulnessRating?.label ?? "N/A"}
              </Badge>
            </div>
          </div>
        </Card>
      ) : null}

      {/* Detailed Metrics */}
      {fullSleep ? (
        <Card padding="lg" variant="default">
          <h3 className="text-lg font-semibold text-neutral-200 mb-4">Detaillierte Metriken</h3>
          <div className="grid gap-4">
            {averageHRV !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-neutral-300">Durchschnittliche HRV</span>
                <Badge className="font-medium" variant="success">
                  {averageHRV.toFixed(1)} ms
                </Badge>
              </div>
            )}
            {maxHRV !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-neutral-300">Maximale HRV</span>
                <Badge className="font-medium" variant="success">
                  {maxHRV.toFixed(1)} ms
                </Badge>
              </div>
            )}
            {averageHeartRate !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-neutral-300">Durchschnittliche Herzfrequenz</span>
                <Badge className="font-medium" variant="info">
                  {averageHeartRate.toFixed(1)} bpm
                </Badge>
              </div>
            )}
            {minHeartRate !== null && (
              <div className="flex items-center justify-between">
                <span className="text-neutral-300">Niedrigste Herzfrequenz</span>
                <Badge
                  className="font-medium"
                  variant={getMinHeartRateRating(minHeartRate).variant}
                >
                  {minHeartRate.toFixed(1)} bpm
                </Badge>
              </div>
            )}
            {fullSleep.average_breath !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-neutral-300">Durchschnittliche Atmung</span>
                <Badge
                  className="font-medium"
                  variant={getBreathingRateRating(fullSleep.average_breath).variant}
                >
                  {fullSleep.average_breath.toFixed(1)} /min
                </Badge>
              </div>
            )}
          </div>
        </Card>
      ) : null}

      {/* Insights */}
      <Card padding="lg" variant="default">
        <h3 className="text-lg font-semibold text-neutral-200 mb-4">Insights</h3>
        <div className="space-y-3 text-neutral-300">
          {renderSleepInsights(sleepScore, combinedSleepData, timingScore)}
        </div>
      </Card>
    </div>
  );
};
